import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { WorkflowActivityActionApiService } from '../services/workflow-activity-action-api.service';
import {
  WorkflowActivityActionType,
  WorkflowTaskActivityActionResponse
} from '../models/workflow-activity-action.model';
import { EntitySelectComponent, EntitySelectPage } from '../../../../shared/components/entity-select/entity-select.component';
import { WorkflowRecordSelect } from '../shared/workflow-record-select.helper';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ContactoApiService } from '../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../tickets/services/tickets-api.service';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../../shared/services/user-directory-api.service';
import { TaskCatalogApiService } from '../../../tasks/services/task-catalog-api.service';
import { TaskApiService } from '../../../tasks/services/task-api.service';
import { TaskResponse } from '../../../tasks/models/task.model';
import { workflowRecordLabel, workflowStatusLabel } from '../models/workflow-labels';

@Component({
  selector: 'app-workflow-activity-action-tester',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, EntitySelectComponent],
  templateUrl: './workflow-activity-action-tester.component.html'
})
export class WorkflowActivityActionTesterComponent implements OnInit, OnDestroy {
  actionType: WorkflowActivityActionType = 'CREATE_TASK';
  objectType = 'CONTACT';
  objectId: number | null = null;
  /** Datos de contexto (JSON) opcionales para sustituir tokens {{...}} en la prueba. */
  contextJson = '';
  showContext = false;

  recordSelect!: WorkflowRecordSelect;
  recordDisplayText = '';

  loading = false;
  error = '';
  result: WorkflowTaskActivityActionResponse | null = null;

  // ── CREATE_TASK ─────────────────────────────────────────────────────────
  taskTitle = 'Llamar a {{contact.firstname}}';
  taskDescription = 'Seguimiento automático por workflow';
  taskType = 'CALL';
  taskPriority = 'HIGH';
  taskDueAmount: number | null = 1;
  taskDueUnit = 'DAYS';
  taskAssigneeType = 'OWNER';
  taskAssigneeUserId: number | null = null;
  taskAssociate = true;
  taskNotes = 'Lead creado desde workflow.';
  taskContinueOnError = false;

  // ── CREATE_NOTE ─────────────────────────────────────────────────────────
  noteText = 'Nota automática: el contacto entró al workflow.';
  noteVisibility = 'INTERNAL';
  noteAssociate = true;
  noteContinueOnError = true;

  // ── COMPLETE_TASK ───────────────────────────────────────────────────────
  completeSelectorType = 'LATEST_PENDING_FOR_RECORD';
  completeTaskId: number | null = null;
  completeTaskName = '';
  completeTitleContains = '';
  completeTaskType = '';
  completionNote = 'Completada automáticamente por workflow.';
  completeContinueOnError = true;

  // ── SCHEDULE_MEETING ────────────────────────────────────────────────────
  meetingTitle = 'Reunión con {{contact.firstname}}';
  meetingDescription = 'Seguimiento automático';
  meetingStartAmount: number | null = 2;
  meetingStartUnit = 'DAYS';
  meetingStartTime = '10:00';
  meetingTimezone = 'America/Lima';
  meetingDuration: number | null = 30;
  meetingOwnerAsOrganizer = true;
  meetingOrganizerUserId: number | null = null;
  meetingEmailProperty = 'email';
  meetingAssociate = true;
  meetingCreateCalendarEvent = true;
  meetingContinueOnError = false;

  readonly actionTypeOptions: { value: WorkflowActivityActionType; label: string }[] = [
    { value: 'CREATE_TASK',      label: 'Crear tarea' },
    { value: 'CREATE_NOTE',      label: 'Crear nota (historial)' },
    { value: 'COMPLETE_TASK',    label: 'Completar tarea' },
    { value: 'SCHEDULE_MEETING', label: 'Programar reunión' }
  ];

  // INC_CRM_020 (8) inc26-46 — `value` = clave exacta del backend, `label` = único texto traducible.
  readonly objectTypeOptions: { value: string; label: string }[] = [
    { value: 'CONTACT', label: 'Contacto' },
    { value: 'COMPANY', label: 'Empresa' },
    { value: 'DEAL', label: 'Negocio' },
    { value: 'QUOTE', label: 'Cotización' },
    { value: 'TICKET', label: 'Ticket' }
  ];
  // Poblados desde el catálogo real del negocio en ngOnInit (value = name que el backend empareja).
  // Estos son el respaldo si el negocio no tiene catálogo: el value NO se traduce por ese emparejamiento.
  taskTypeOptions: { value: string; label: string }[] = [
    { value: 'CALL', label: 'Llamada' },
    { value: 'EMAIL', label: 'Correo' },
    { value: 'MEETING', label: 'Reunión' },
    { value: 'GENERAL', label: 'General' }
  ];
  priorityOptions: { value: string; label: string }[] = [
    { value: 'LOW', label: 'Baja' },
    { value: 'MEDIUM', label: 'Media' },
    { value: 'HIGH', label: 'Alta' }
  ];
  readonly dueUnitOptions: { value: string; label: string }[] = [
    { value: 'MINUTES', label: 'Minutos' },
    { value: 'HOURS', label: 'Horas' },
    { value: 'DAYS', label: 'Días' },
    { value: 'WEEKS', label: 'Semanas' }
  ];
  readonly visibilityOptions: { value: string; label: string }[] = [
    { value: 'INTERNAL', label: 'Interna' },
    { value: 'PUBLIC', label: 'Pública' }
  ];
  readonly completeSelectorOptions = [
    { value: 'LATEST_PENDING_FOR_RECORD', label: 'Última tarea pendiente del registro' },
    { value: 'EXPLICIT',                  label: 'Tarea específica (por ID)' },
    { value: 'TITLE_CONTAINS',            label: 'Por título que contiene…' }
  ];

  allUsers: DirectoryUser[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private activityApi: WorkflowActivityActionApiService,
    private authService: AuthService,
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealApi: DealApiService,
    private quoteApi: QuoteApiService,
    private ticketsApi: TicketsApiService,
    private userDirectory: UserDirectoryApiService,
    private taskCatalog: TaskCatalogApiService,
    private taskApi: TaskApiService
  ) {}

  ngOnInit(): void {
    this.recordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.objectType,
      () => this.authService.getCurrentUser()?.businessId || 1
    );
    // Directorio de la empresa: sin `users:VIEW` y con el negocio resuelto desde el token.
    this.userDirectory.searchUsers('', 200).subscribe({
      next: (users) => { this.allUsers = users; },
      error: () => {}
    });
    // Catálogo real de tipos/prioridades (el backend empareja config.type/priority por name|label).
    this.taskCatalog.getActiveTypes().subscribe({
      next: (list) => {
        const opts = (list ?? []).filter((t) => t.name).map((t) => ({ value: t.name, label: t.label || t.name }));
        if (opts.length) {
          this.taskTypeOptions = opts;
          if (!opts.some((o) => o.value === this.taskType)) { this.taskType = opts[0].value; }
        }
      },
      error: () => {}
    });
    this.taskCatalog.getActivePriorities().subscribe({
      next: (list) => {
        const opts = (list ?? []).filter((p) => p.name).map((p) => ({ value: p.name, label: p.label || p.name }));
        if (opts.length) {
          this.priorityOptions = opts;
          if (!opts.some((o) => o.value === this.taskPriority)) { this.taskPriority = opts[0].value; }
        }
      },
      error: () => {}
    });
  }

  // ── COMPLETE_TASK · búsqueda de tarea por título (EXPLICIT) ────────────────
  taskSearch = (term: string, page: number): Observable<EntitySelectPage<TaskResponse>> => {
    return this.taskApi.filterTasks({ searchTerm: term || undefined, page, size: 10 }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as TaskResponse[], totalPages: 0 }))
    );
  };
  taskLabel = (t: TaskResponse): string => t.title || `Tarea #${t.id}`;
  taskSublabel = (t: TaskResponse): string => (t.id != null ? `#${t.id}` : '');
  onCompleteTaskSelected(t: TaskResponse | null): void {
    this.completeTaskName = t ? this.taskLabel(t) : '';
  }

  onObjectTypeChange(): void {
    this.objectId = null;
    this.recordDisplayText = '';
  }

  onRecordSelected(item: any | null): void {
    this.recordDisplayText = item ? this.recordSelect.label(item) : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Buscador de usuarios (asignado / organizador) ─────────────────────────
  userSearch = (term: string): Observable<EntitySelectPage<DirectoryUser>> =>
    this.userDirectory.searchUsers(term, 20).pipe(
      map((items) => ({ items, totalPages: 1 })),
      catchError(() => of({ items: [] as DirectoryUser[], totalPages: 0 }))
    );
  userLabel = (u: DirectoryUser): string => directoryUserLabel(u);
  userSublabel = (u: DirectoryUser): string => u.mail ?? '';
  userNameById(id: number | null | undefined): string {
    if (id == null) return '';
    const u = this.allUsers.find((x) => x.id === id);
    // La lista solo trae ACTIVOS: no estar aquí significa dado de baja, no «#id».
    return u ? this.userLabel(u) : 'Usuario no disponible';
  }

  /** Construye el objeto config completo (todos los campos requeridos) según el tipo de acción. */
  private buildConfig(): Record<string, any> {
    switch (this.actionType) {
      case 'CREATE_TASK':
        return {
          title: this.taskTitle,
          description: this.taskDescription,
          type: this.taskType,
          priority: this.taskPriority,
          due: { type: 'RELATIVE', amount: this.taskDueAmount ?? 0, unit: this.taskDueUnit },
          assignee: this.taskAssigneeType === 'SPECIFIC_USER' && this.taskAssigneeUserId != null
            ? { type: 'SPECIFIC_USER', userId: this.taskAssigneeUserId }
            : { type: this.taskAssigneeType },
          associateToCurrentRecord: this.taskAssociate,
          notes: this.taskNotes,
          continueOnError: this.taskContinueOnError
        };
      case 'CREATE_NOTE':
        return {
          text: this.noteText,
          visibility: this.noteVisibility,
          associateToCurrentRecord: this.noteAssociate,
          continueOnError: this.noteContinueOnError
        };
      case 'COMPLETE_TASK':
        return {
          taskSelector: {
            type: this.completeSelectorType,
            taskId: this.completeSelectorType === 'EXPLICIT' ? (this.completeTaskId ?? null) : null,
            titleContains: this.completeSelectorType === 'TITLE_CONTAINS' ? (this.completeTitleContains || null) : null,
            taskType: this.completeTaskType || null
          },
          completionNote: this.completionNote,
          continueOnError: this.completeContinueOnError
        };
      case 'SCHEDULE_MEETING':
        return {
          title: this.meetingTitle,
          description: this.meetingDescription,
          start: {
            type: 'RELATIVE',
            amount: this.meetingStartAmount ?? 0,
            unit: this.meetingStartUnit,
            time: this.meetingStartTime,
            timezone: this.meetingTimezone
          },
          durationMinutes: this.meetingDuration ?? 30,
          ownerAsOrganizer: this.meetingOwnerAsOrganizer,
          organizerUserId: this.meetingOwnerAsOrganizer ? null : (this.meetingOrganizerUserId ?? null),
          attendees: [{ type: 'CURRENT_RECORD_EMAIL', emailProperty: this.meetingEmailProperty || 'email' }],
          associateToCurrentRecord: this.meetingAssociate,
          createCalendarEvent: this.meetingCreateCalendarEvent,
          continueOnError: this.meetingContinueOnError
        };
      default:
        return {};
    }
  }

  test(): void {
    let context: Record<string, any> = {};
    if (this.contextJson.trim()) {
      try { context = JSON.parse(this.contextJson); }
      catch { this.error = 'Los datos de contexto no son un JSON válido.'; return; }
    }

    this.loading = true;
    this.error = '';
    this.result = null;

    this.activityApi.test({
      actionType: this.actionType,
      objectType: this.objectType,
      ...(this.objectId ? { objectId: this.objectId } : {}),
      config: this.buildConfig(),
      context
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => { this.result = res; this.loading = false; },
      error: (err) => {
        const msg = err.error?.message ?? '';
        if (err.status === 403) this.error = 'No tienes permisos para realizar esta acción.';
        else if (msg.toLowerCase().includes('ticket')) this.error = 'No se pudo procesar la solicitud sobre el ticket.';
        else this.error = msg || 'Error al ejecutar la acción de actividad.';
        this.loading = false;
      }
    });
  }

  /**
   * INC_CRM_020 (8) inc26-46 — el badge pintaba el enum crudo del backend (SUCCESS/FAILED/SKIPPED)
   * y la fila de detalle el `actionType` técnico. El valor que llega NO se toca; solo se traduce
   * lo que se muestra, y si aparece un estado/tipo desconocido se enseña tal cual.
   */
  /** Nombre del registro afectado: antes se pintaba «CONTACT #42». */
  readonly recordText = workflowRecordLabel;

  /**
   * Estado en español. El vocabulario vive en el diccionario único del módulo
   * (`models/workflow-labels.ts`); antes cada probador repetía su propio `switch` de tres casos y
   * cualquier otro estado del motor (`RETRY_SCHEDULED`, `PENDING`…) se colaba en crudo.
   */
  statusLabel(status: string | undefined): string {
    return workflowStatusLabel(status, 'Desconocido');
  }

  actionTypeLabel(type: string | undefined): string {
    if (!type) {
      return '—';
    }
    return this.actionTypeOptions.find((o) => o.value === type)?.label ?? type;
  }

  getStatusClass(status: string | undefined): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS': return 'bg-green-100 text-green-700';
      case 'FAILED':  return 'bg-red-100 text-red-700';
      case 'SKIPPED': return 'bg-gray-100 text-gray-600';
      case 'NOT_REQUESTED': return 'bg-yellow-100 text-yellow-700';
      default:        return 'bg-blue-100 text-blue-700';
    }
  }

  get resultJson(): string {
    return JSON.stringify(this.result?.output ?? {}, null, 2);
  }

  get errorJson(): string {
    return JSON.stringify(this.result?.error ?? {}, null, 2);
  }

  get isCreateTask(): boolean { return this.actionType === 'CREATE_TASK'; }
  get isCreateNote(): boolean { return this.actionType === 'CREATE_NOTE'; }
  get isCompleteTask(): boolean { return this.actionType === 'COMPLETE_TASK'; }
  get isScheduleMeeting(): boolean { return this.actionType === 'SCHEDULE_MEETING'; }
}
